'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { MOCK_TRIPS } from '@/lib/mockData';
import {
  MapPin, Clock, Users, Calendar, Star, Shield, ChevronDown, ChevronUp,
  Check, X, ArrowRight, Share2, Heart, ChevronLeft, ChevronRight,
  Mountain, AlertTriangle, Phone, Mail, Sparkles
} from 'lucide-react';
import BookingModal from './BookingModal';

const difficultyColors: Record<string, string> = {
  Easy: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Moderate: 'bg-amber-50 text-amber-700 border-amber-200',
  Challenging: 'bg-orange-50 text-orange-700 border-orange-200',
  Extreme: 'bg-red-50 text-red-700 border-red-200',
};

export default function TripDetailContent() {
  const trip = MOCK_TRIPS[0];
  const [activeImage, setActiveImage] = useState(0);
  const [openItineraryDays, setOpenItineraryDays] = useState<number[]>([1]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [wishlist, setWishlist] = useState(false);

  const seatsLeft = trip.totalSeats - trip.bookedSeats;
  const isSoldOut = seatsLeft === 0;
  const isUrgent = seatsLeft > 0 && seatsLeft <= 4;

  const toggleItineraryDay = (day: number) => {
    setOpenItineraryDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const relatedTrips = MOCK_TRIPS.filter((t) => t.id !== trip.id && t.category === trip.category).slice(0, 3);

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link href="/homepage" className="hover:text-[#E8440A] transition-colors">Home</Link>
        <span>/</span>
        <Link href="/trips-listing-page" className="hover:text-[#E8440A] transition-colors">Trips</Link>
        <span>/</span>
        <span className="text-gray-800 font-medium truncate">{trip.title}</span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="xl:col-span-2">
          {/* Image Gallery */}
          <div className="mb-8">
            <div className="relative rounded-2xl overflow-hidden h-72 sm:h-96 lg:h-[480px] mb-3 shadow-2xl shadow-black/20">
              <AppImage
                src={trip.images[activeImage]}
                alt={`${trip.title} — image ${activeImage + 1} of ${trip.images.length}`}
                fill
                priority
                className="object-cover transition-opacity duration-300"
                sizes="(max-width: 1280px) 100vw, 66vw"
              />
              {/* Glossy overlay shimmer */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-white/5 pointer-events-none" />
              <button
                onClick={() => setActiveImage((prev) => (prev - 1 + trip.images.length) % trip.images.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-xl p-2.5 shadow-lg shadow-black/20 transition-all hover:scale-110 backdrop-blur-sm border border-white/60"
              >
                <ChevronLeft size={18} className="text-gray-700" />
              </button>
              <button
                onClick={() => setActiveImage((prev) => (prev + 1) % trip.images.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-xl p-2.5 shadow-lg shadow-black/20 transition-all hover:scale-110 backdrop-blur-sm border border-white/60"
              >
                <ChevronRight size={18} className="text-gray-700" />
              </button>
              <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                {activeImage + 1} / {trip.images.length}
              </div>
            </div>
            <div className="flex gap-2">
              {trip.images.map((img, i) => (
                <button
                  key={`thumb-${i}`}
                  onClick={() => setActiveImage(i)}
                  className={`relative w-20 h-16 rounded-xl overflow-hidden border-2 transition-all hover:scale-105 ${activeImage === i ? 'border-[#E8440A] shadow-md shadow-orange-200' : 'border-transparent hover:border-gray-300'}`}
                >
                  <AppImage
                    src={img}
                    alt={`${trip.title} thumbnail ${i + 1}`}
                    fill
                    className="object-cover"
                    sizes="80px"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Title + Meta */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${difficultyColors[trip.difficulty]}`}>
                    {trip.difficulty}
                  </span>
                  <span className="bg-[#1A3C5E]/10 text-[#1A3C5E] text-xs font-semibold px-2.5 py-1 rounded-full">
                    {trip.category}
                  </span>
                  {trip.featured && (
                    <span className="bg-gradient-to-r from-[#E8440A] to-[#ff6b35] text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm shadow-orange-300 flex items-center gap-1">
                      <Sparkles size={10} /> Featured
                    </span>
                  )}
                </div>
                <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1A3C5E] leading-tight">{trip.title}</h1>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setWishlist(!wishlist)}
                  className={`p-2.5 rounded-xl border transition-all hover:scale-110 ${wishlist ? 'border-red-200 bg-gradient-to-br from-red-50 to-red-100 text-red-500 shadow-sm shadow-red-200' : 'border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-400 bg-white'}`}
                >
                  <Heart size={18} className={wishlist ? 'fill-red-500' : ''} />
                </button>
                <button className="p-2.5 rounded-xl border border-gray-200 text-gray-400 hover:text-gray-600 transition-all hover:scale-110 bg-white hover:bg-gray-50">
                  <Share2 size={18} />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#E8440A]" />{trip.location}, {trip.state}</span>
              <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#E8440A]" />{trip.duration} days</span>
              <span className="flex items-center gap-1.5"><Users size={14} className="text-[#E8440A]" />Max {trip.maxGroupSize} people</span>
              <span className="flex items-center gap-1.5"><Mountain size={14} className="text-[#E8440A]" />Min age {trip.minAge}+</span>
              <span className="flex items-center gap-1.5">
                <Calendar size={14} className="text-[#E8440A]" />
                {new Date(trip.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                {' → '}
                {new Date(trip.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="card-base p-6 mb-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <h2 className="text-lg font-bold text-[#1A3C5E] mb-3">About This Trip</h2>
            <p className="text-gray-600 leading-relaxed">{trip.description}</p>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {trip.highlights.map((h) => (
                <div key={`highlight-${h}`} className="flex items-center gap-2 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-3 border border-orange-100/60">
                  <Star size={14} className="text-[#E8440A] shrink-0" />
                  <span className="text-xs font-medium text-gray-700">{h}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Inclusions / Exclusions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="card-base p-5 border border-emerald-100/60 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="font-bold text-[#1A3C5E] mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-sm shadow-emerald-300">
                  <Check size={12} className="text-white" />
                </span>
                What's Included
              </h3>
              <ul className="flex flex-col gap-2">
                {trip.inclusions.map((item) => (
                  <li key={`inc-${item}`} className="flex items-start gap-2 text-sm text-gray-600">
                    <Check size={13} className="text-emerald-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-base p-5 border border-red-100/60 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="font-bold text-[#1A3C5E] mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center shadow-sm shadow-red-300">
                  <X size={12} className="text-white" />
                </span>
                Not Included
              </h3>
              <ul className="flex flex-col gap-2">
                {trip.exclusions.map((item) => (
                  <li key={`exc-${item}`} className="flex items-start gap-2 text-sm text-gray-600">
                    <X size={13} className="text-red-400 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Itinerary */}
          <div className="card-base p-6 mb-6 border border-gray-100 shadow-sm">
            <h2 className="text-lg font-bold text-[#1A3C5E] mb-4">Day-by-Day Itinerary</h2>
            <div className="flex flex-col gap-2">
              {trip.itinerary.map((day) => (
                <div key={`day-${day.day}`} className="border border-gray-100 rounded-xl overflow-hidden hover:border-orange-200 transition-colors">
                  <button
                    onClick={() => toggleItineraryDay(day.day)}
                    className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-orange-50/40 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-gradient-to-br from-[#E8440A] to-[#c73508] text-white text-xs font-bold rounded-full flex items-center justify-center shrink-0 shadow-sm shadow-orange-300">
                        {day.day}
                      </span>
                      <span className="font-semibold text-gray-800 text-sm">{day.title}</span>
                    </div>
                    {openItineraryDays.includes(day.day) ? (
                      <ChevronUp size={16} className="text-gray-400 shrink-0" />
                    ) : (
                      <ChevronDown size={16} className="text-gray-400 shrink-0" />
                    )}
                  </button>
                  {openItineraryDays.includes(day.day) && (
                    <div className="px-4 pb-4 pt-1 bg-orange-50/20">
                      <p className="text-gray-600 text-sm leading-relaxed pl-11">{day.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Meeting Point + Cancellation */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="card-base p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="font-bold text-[#1A3C5E] mb-2 flex items-center gap-2">
                <MapPin size={15} className="text-[#E8440A]" /> Meeting Point
              </h3>
              <p className="text-gray-600 text-sm">{trip.meetingPoint}</p>
            </div>
            <div className="card-base p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="font-bold text-[#1A3C5E] mb-2 flex items-center gap-2">
                <Shield size={15} className="text-[#E8440A]" /> Cancellation Policy
              </h3>
              <p className="text-gray-600 text-sm">{trip.cancellationPolicy}</p>
            </div>
          </div>

          {/* Related Trips */}
          {relatedTrips.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-[#1A3C5E] mb-4">Similar Trips You Might Like</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedTrips.map((t) => (
                  <Link key={t.id} href="/trip-detail-page" className="card-base trip-card-hover overflow-hidden block shadow-sm hover:shadow-lg transition-shadow">
                    <div className="relative h-32">
                      <AppImage
                        src={t.images[0]}
                        alt={`${t.title} — related trip in ${t.location}`}
                        fill
                        className="object-cover"
                        sizes="33vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                    </div>
                    <div className="p-3">
                      <p className="font-bold text-[#1A3C5E] text-sm line-clamp-1">{t.title}</p>
                      <p className="text-gray-500 text-xs mt-0.5">{t.location} · {t.duration} days</p>
                      <p className="text-[#E8440A] font-bold font-mono text-sm mt-1">₹{t.price.toLocaleString('en-IN')}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sticky Booking Widget */}
        <div className="xl:col-span-1">
          <div className="sticky top-24">
            {/* Main Booking Card — premium glass-morphism style */}
            <div className="relative rounded-2xl overflow-hidden mb-4 shadow-xl shadow-orange-100/60">
              {/* Gradient top accent bar */}
              <div className="h-1.5 w-full bg-gradient-to-r from-[#E8440A] via-[#ff6b35] to-[#E8440A] bg-[length:200%_100%]" style={{backgroundSize:'200% 100%'}} />
              <div className="bg-white p-6 border border-gray-100/80">
                {/* Price */}
                <div className="flex items-end justify-between mb-1">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5 uppercase tracking-wide font-medium">Starting from</p>
                    <p className="text-3xl font-extrabold bg-gradient-to-r from-[#E8440A] to-[#c73508] bg-clip-text text-transparent font-mono">
                      ₹{trip.price.toLocaleString('en-IN')}
                    </p>
                  </div>
                  <p className="text-gray-400 text-sm mb-1">per person</p>
                </div>

                {/* Seats Status */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                    <span>{trip.bookedSeats} of {trip.totalSeats} seats booked</span>
                    <span className={isUrgent ? 'text-red-500 font-bold' : 'text-emerald-600 font-medium'}>
                      {isSoldOut ? 'SOLD OUT' : `${seatsLeft} left`}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        (trip.bookedSeats / trip.totalSeats) >= 0.9 ? 'bg-gradient-to-r from-red-400 to-red-600' :
                        (trip.bookedSeats / trip.totalSeats) >= 0.7 ? 'bg-gradient-to-r from-amber-400 to-amber-600' : 'bg-gradient-to-r from-emerald-400 to-emerald-600'
                      }`}
                      style={{ width: `${(trip.bookedSeats / trip.totalSeats) * 100}%` }}
                    />
                  </div>
                  {isUrgent && (
                    <p className="text-red-500 text-xs font-semibold mt-1.5 flex items-center gap-1">
                      <AlertTriangle size={11} /> Only {seatsLeft} seat{seatsLeft !== 1 ? 's' : ''} remaining!
                    </p>
                  )}
                </div>

                {/* Trip Info Summary */}
                <div className="flex flex-col gap-2 py-4 border-y border-gray-100 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Departure</span>
                    <span className="font-semibold text-gray-800">
                      {new Date(trip.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Duration</span>
                    <span className="font-semibold text-gray-800">{trip.duration} days / {trip.duration - 1} nights</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Difficulty</span>
                    <span className="font-semibold text-gray-800">{trip.difficulty}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Min Age</span>
                    <span className="font-semibold text-gray-800">{trip.minAge}+ years</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Group Size</span>
                    <span className="font-semibold text-gray-800">Max {trip.maxGroupSize}</span>
                  </div>
                </div>

                {/* Book Button — premium shiny CTA */}
                {isSoldOut ? (
                  <button disabled className="w-full bg-gray-200 text-gray-400 font-bold py-3.5 rounded-xl cursor-not-allowed text-sm">
                    Sold Out — Join Waitlist
                  </button>
                ) : (
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="relative w-full overflow-hidden group flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-bold text-base text-white transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-orange-400/40 hover:shadow-xl hover:shadow-orange-400/50"
                    style={{
                      background: 'linear-gradient(135deg, #ff6b35 0%, #E8440A 40%, #c73508 100%)',
                    }}
                  >
                    {/* Shiny gloss overlay */}
                    <span className="absolute inset-0 bg-gradient-to-b from-white/25 via-white/5 to-transparent pointer-events-none rounded-xl" />
                    {/* Shimmer sweep */}
                    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 pointer-events-none" />
                    <span className="relative flex items-center gap-2">
                      Book Now — ₹{trip.price.toLocaleString('en-IN')} <ArrowRight size={18} />
                    </span>
                  </button>
                )}

                <p className="text-center text-xs text-gray-400 mt-3">
                  🔒 Secure payment via Razorpay. No hidden charges.
                </p>
              </div>
            </div>

            {/* Contact Box */}
            <div className="rounded-2xl overflow-hidden shadow-md border border-gray-100">
              <div className="h-1 w-full bg-gradient-to-r from-[#1A3C5E] to-[#2a5a8e]" />
              <div className="bg-white p-4">
                <p className="text-sm font-bold text-[#1A3C5E] mb-3">Need Help? Talk to Us</p>
                <div className="flex flex-col gap-2">
                  <a href="tel:+916381503933" className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-[#E8440A] transition-colors group">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-orange-200 transition-shadow">
                      <Phone size={14} className="text-[#E8440A]" />
                    </div>
                    +91 6381503933
                  </a>
                  <a href="mailto:hello@vinciadventures.com" className="flex items-center gap-2.5 text-sm text-gray-600 hover:text-[#E8440A] transition-colors group">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-orange-200 transition-shadow">
                      <Mail size={14} className="text-[#E8440A]" />
                    </div>
                    hello@vinciadventures.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModal trip={trip} onClose={() => setShowBookingModal(false)} />
      )}
    </div>
  );
}